import React from 'react';
import { Mail, Bell, Phone } from 'lucide-react';

const EMAIL_ITEMS = [
  { key: 'email_job_alerts', label: 'Job Alerts', desc: 'New jobs matching your skills' },
  { key: 'email_application_updates', label: 'Application Updates', desc: 'Status changes on your applications' },
  { key: 'email_messages', label: 'Messages', desc: 'New messages from employers' },
  { key: 'email_marketing', label: 'Marketing & Tips', desc: 'Platform news and career tips' },
];

const PUSH_ITEMS = [
  { key: 'push_job_alerts', label: 'Job Alerts', desc: 'Instant notifications for new jobs' },
  { key: 'push_application_updates', label: 'Application Updates', desc: 'Real-time status updates' },
  { key: 'push_messages', label: 'Messages', desc: 'Instant message notifications' },
];

function NotificationToggle({
  item, checked, onChange, color,
}) {
  const colorClass = {
    blue: 'text-blue-600 focus:ring-blue-500',
    purple: 'text-purple-600 focus:ring-purple-500',
    emerald: 'text-emerald-600 focus:ring-emerald-500',
  }[color] || 'text-blue-600 focus:ring-blue-500';

  return (
    <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition">
      <div>
        <p className="font-medium text-gray-900">{item.label}</p>
        <p className="text-sm text-gray-500">{item.desc}</p>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className={`w-5 h-5 rounded border-gray-300 ${colorClass}`}
      />
    </label>
  );
}

/**
 * Notifications tab in Worker Settings. Owns display only.
 * State + persistence live in parent.
 */
export default function NotificationsSection({
  notifications,
  setNotifications,
  saving,
  onSave,
  reminderPref,
  setReminderPref,
  reminderSaving,
  onSaveReminderPref,
}) {
  const setChecked = (key) => (e) => setNotifications({ ...notifications, [key]: e.target.checked });

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-800">
          <Bell className="w-4 h-4 inline mr-2" />
          Manage how you receive notifications about jobs, applications, and messages.
        </p>
      </div>

      {/* Email Notifications */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600" />
          Email Notifications
        </h3>
        {EMAIL_ITEMS.map((item) => (
          <NotificationToggle
            key={item.key}
            item={item}
            checked={notifications[item.key]}
            onChange={setChecked(item.key)}
            color="blue"
          />
        ))}
      </div>

      {/* Push Notifications */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Bell className="w-5 h-5 text-purple-600" />
          Push Notifications
        </h3>
        {PUSH_ITEMS.map((item) => (
          <NotificationToggle
            key={item.key}
            item={item}
            checked={notifications[item.key]}
            onChange={setChecked(item.key)}
            color="purple"
          />
        ))}
      </div>

      {/* SMS Notifications */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Phone className="w-5 h-5 text-emerald-600" />
          SMS Notifications
        </h3>
        <NotificationToggle
          item={{ label: 'Urgent Alerts', desc: 'Critical updates like shift cancellations' }}
          checked={notifications.sms_urgent_alerts}
          onChange={setChecked('sms_urgent_alerts')}
          color="emerald"
        />
      </div>

      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50"
        data-testid="save-notifications-btn"
      >
        {saving ? 'Saving...' : 'Save Preferences'}
      </button>

      {/* Personal shift-reminder override (iter77-B) */}
      <div
        className="mt-8 pt-6 border-t border-slate-200"
        data-testid="worker-reminder-preference-section"
      >
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-5 h-5 text-emerald-600" />
          <h3 className="text-lg font-bold text-slate-900">Shift Reminder Preference</h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          How far ahead should we remind you before a shift starts? This overrides the
          employer&apos;s default so long-commute shifts can give you extra lead time.
        </p>

        <label className="flex items-center gap-3 mb-4">
          <input
            type="checkbox"
            checked={reminderPref.reminder_enabled}
            onChange={(e) => setReminderPref((p) => ({ ...p, reminder_enabled: e.target.checked }))}
            data-testid="worker-reminder-enabled-toggle"
            className="w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
          />
          <span className="text-sm font-medium text-slate-800">Receive shift reminders</span>
        </label>

        <label htmlFor="worker-reminder-window-select" className="block text-sm font-medium text-slate-700 mb-1">
          Remind me…
        </label>
        <select
          id="worker-reminder-window-select"
          value={reminderPref.reminder_hours_before === null ? '' : String(reminderPref.reminder_hours_before)}
          onChange={(e) => {
            const v = e.target.value;
            setReminderPref((p) => ({
              ...p,
              reminder_hours_before: v === '' ? null : parseFloat(v),
            }));
          }}
          disabled={!reminderPref.reminder_enabled}
          data-testid="worker-reminder-window-select"
          className="w-full px-4 py-3 bg-white border border-slate-300 rounded-xl text-slate-900 focus:ring-2 focus:ring-emerald-500 disabled:bg-slate-100 disabled:text-slate-400 mb-4"
        >
          <option value="">Use employer&apos;s default</option>
          <option value="0.5">30 minutes before</option>
          <option value="1">1 hour before</option>
          <option value="2">2 hours before</option>
          <option value="3">3 hours before</option>
          <option value="4">4 hours before</option>
        </select>

        <button
          type="button"
          onClick={onSaveReminderPref}
          disabled={reminderSaving}
          data-testid="save-worker-reminder-preference-btn"
          className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
        >
          {reminderSaving ? 'Saving…' : 'Save Reminder Preference'}
        </button>
      </div>
    </div>
  );
}
